import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PI_STATUSES } from "../../../constants/inventoryConstants";
import piAPI from "../../../api/inventory/piApi";
import { Link, useSearchParams } from "react-router-dom";
import RowsPerPage from "../../../components/partials/RowsPerPage";
import Pagination from "../../../components/partials/Pagination";
import PurchaseInvoiceRow from "./PurchaseInvoiceRow";
import TableShimmer from "../../../components/partials/TableShimmer";
import StockUpdateModal from "../../../components/partials/inventory/StockUpdateModal";

function PurchaseInvoiceListPage({ onCreate, onOpen }) {
  const [tableLoading, setTableLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);

  const query = useMemo(
    () => ({
      wareHouseId: searchParams.get("wareHouseId") || "",
      search: searchParams.get("search") || "",
      status: searchParams.get("status") || "",
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      datefrom: searchParams.get("datefrom") || "",
      dateto: searchParams.get("dateto") || "",
    }),
    [searchParams],
  );

  const searchTimeout = useRef();

  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 400);
  };

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const res = await piAPI.list(query);

      if (res.code === 200) {
        const { purchaseInvoice, pagination } = res.data;

        setItems(purchaseInvoice);
        setPagination(pagination);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [query]);

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);

      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }

      if (key !== "page") {
        params.set("page", 1);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Purchase Invoices</h4>
              <h6>Manage supplier invoices and stock receipts</h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/purchase-invoices/add-new" className="btn btn-primary">
              <i className="ti ti-plus me-1" /> Create Invoice
            </Link>
          </div>
        </div>

        <div className="welcome align-items-center justify-content-between">
          <div className="row">
            <div className="col-md-3">
              <label className="form-label small">Status</label>
              <select
                className="form-select"
                value={searchParams.get("status")}
                onChange={(e) => updateFilter("status", e.target.value)}
              >
                <option value="all">All Statuses</option>
                {Object.entries(PI_STATUSES).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small">From Date</label>
              <input
                type="date"
                className="form-control"
                value={searchParams.get("datefrom")}
                onChange={(e) => updateFilter("datefrom", e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small">To Date</label>
              <input
                type="date"
                className="form-control"
                value={searchParams.get("dateto")}
                onChange={(e) => updateFilter("dateto", e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Search SKU, Barcode</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search SKU, barcode, or product name..."
                defaultValue={query.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4>Inventory List</h4>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>Invoice #</th>
                    <th>Supplier</th>
                    <th>Location</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={8} />
                  ) : items.length > 0 ? (
                    items.map((item, index) => (
                      <PurchaseInvoiceRow
                        key={item._id}
                        index={index}
                        item={item}
                        piStatus={PI_STATUSES}
                        page={query.page}
                        limit={query.limit}
                        onOpen={onOpen}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted p-4">
                        No inventory found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <StockUpdateModal
        isOpen={modalOpen}
        mode="pi"
        defaultLocationId={
          query.wareHouseId !== "all" ? query.wareHouseId : undefined
        }
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          setModalOpen(false);
          fetchData();
        }}
      />
    </>
  );
}

export default PurchaseInvoiceListPage;
